import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

/**
 * Kept in sync with the inline restore script in `snippets/product-grid.liquid`, which
 * applies the stored value before first paint.
 */
const HOVER_PREVIEW_STORAGE_KEY = 'product-hover-preview';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
  }

  /**
   * Swaps every product card preview between its normal and its hover image.
   *
   * @param {Event} event
   */
  toggleHoverPreview({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#setHoverPreview(target.checked);

    requestIdleCallback(() => {
      sessionStorage.setItem(HOVER_PREVIEW_STORAGE_KEY, target.checked ? 'on' : 'off');
    });
  }

  /**
   * Sets the hover preview state on the grid and re-latches every card that is already rendered.
   *
   * @param {boolean} enabled
   */
  #setHoverPreview(enabled) {
    const { grid } = this.refs;

    if (!grid) return;

    grid.setAttribute('data-hover-preview', enabled ? 'on' : 'off');
    this.#applyHoverPreviewToCards();
  }

  /**
   * Re-latches every card that is currently rendered onto the image the switch asks for.
   */
  #applyHoverPreviewToCards() {
    const { grid } = this.refs;

    if (!grid) return;

    // Duck-typed instead of `instanceof ProductCard` so results-list.js does not have to
    // import product-card.js just to flip an image.
    for (const card of grid.querySelectorAll('product-card')) {
      const applyHoverPreview = /** @type {any} */ (card).applyHoverPreview;

      if (typeof applyHoverPreview === 'function') applyHoverPreview.call(card);
    }
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
